import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FilePdf, Image as ImageIcon, Paperclip, Trash, UploadSimple, Play, MusicNote, Download, X, ArrowsOut } from "@phosphor-icons/react";

/**
 * Chantier documents panel — upload + list + inline previews.
 *
 * Every previewable type (photo, video, pdf) opens in an internal lightbox so
 * the tech never leaves the PWA. Non-previewable formats (dwg, docx, zip, xlsx…)
 * fall back to a native browser download via `<a download>`.
 *
 * The panel derives the media "kind" at render time from `content_type` +
 * filename extension rather than trusting the stored `kind` field — old
 * records uploaded before the video/audio classifier still open correctly
 * (screenshots stored as `kind: file` are re-classified as photos here).
 */
export default function DocumentsSection({ chantierId, isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [preview, setPreview] = useState(null); // { doc }
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/chantiers/${chantierId}/documents`);
      setDocs(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, [chantierId]);

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    const files = Array.from(fileList);
    let ok = 0;
    let fail = 0;
    // Sequential to avoid saturating the mobile upstream on flaky 4G links.
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (label) fd.append("label", label);
        await api.post(`/chantiers/${chantierId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (err) {
        fail += 1;
        console.debug("[docs] upload failed:", file.name, formatApiError(err));
      }
    }
    setBusy(false);
    setLabel("");
    if (ok > 0) toast.success(ok === 1 ? "Document ajouté" : `${ok} documents ajoutés`);
    if (fail > 0) toast.error(fail === 1 ? "1 fichier a échoué" : `${fail} fichiers ont échoué`);
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer "${d.filename}" ?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 space-y-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Libellé optionnel (ex : Notice PAC, plan élec, vidéo montage…)"
          className="bg-white/5 border-white/10 h-10 text-sm"
          data-testid="doc-label"
        />
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.dwg,.dxf,.zip"
          onChange={(e) => e.target.files && upload(e.target.files)}
        />
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="w-full bg-[#005CFF] hover:bg-[#0047CC] h-11 rounded-xl"
          data-testid="doc-upload-btn"
        >
          <UploadSimple size={16} weight="bold" /> {busy ? "Upload…" : "Ajouter un document"}
        </Button>
        <div className="text-[10px] text-white/40 text-center">
          PDF, images, vidéos MP4/MOV, notices Word/Excel, plans DWG/DXF, ZIP · sélection multiple possible
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/10 p-8 text-center">
          <Paperclip size={26} className="mx-auto text-white/40 mb-2" weight="duotone" />
          <div className="text-sm text-white/60">Aucun document</div>
          <div className="text-[10px] text-white/40 mt-1">
            Notice PAC, plan élec, vidéo montage, photo diagnostique…
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <DocRow
              key={d.id}
              doc={d}
              onPreview={() => setPreview({ doc: d })}
              onRemove={isAdmin ? () => remove(d) : null}
            />
          ))}
        </div>
      )}

      {preview && <MediaLightbox doc={preview.doc} onClose={() => setPreview(null)} />}
    </div>
  );
}

/**
 * Compute the effective media kind from content_type + filename extension.
 * Old records may have `kind: file` even for images/PDFs uploaded before the
 * classifier was added — always compute a fresh view at render time.
 */
function effectiveKind(doc) {
  const ct = (doc.content_type || "").toLowerCase();
  const fn = (doc.filename || "").toLowerCase();
  if (ct.startsWith("image/") || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(fn)) return "photo";
  if (ct.startsWith("video/") || /\.(mp4|m4v|mov|webm|mkv|avi|3gp)$/i.test(fn)) return "video";
  if (ct.startsWith("audio/") || /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(fn)) return "audio";
  if (ct === "application/pdf" || fn.endsWith(".pdf")) return "pdf";
  return "file";
}

const ICON_FOR = {
  photo: ImageIcon,
  pdf: FilePdf,
  video: Play,
  audio: MusicNote,
  file: Paperclip,
};
const COLOR_FOR = {
  photo: "#005CFF",
  pdf: "#EF4444",
  video: "#8B5CF6",
  audio: "#10B981",
  file: "#9CA3AF",
};

function DocRow({ doc, onPreview, onRemove }) {
  const kind = effectiveKind(doc);
  const Icon = ICON_FOR[kind];
  const c = COLOR_FOR[kind];
  const previewable = kind === "photo" || kind === "video" || kind === "pdf";
  const url = fileUrl(doc.id);

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`doc-${doc.id}`}>
      {/* Inline mini-preview so techs recognise the doc at a glance */}
      {kind === "video" && (
        <video
          controls
          playsInline
          preload="metadata"
          src={url}
          className="w-full max-h-64 bg-black object-contain"
          data-testid={`doc-video-${doc.id}`}
        />
      )}
      <div className="p-3 flex items-center gap-3">
        <button
          type="button"
          onClick={previewable ? onPreview : null}
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 active:scale-95 transition"
          style={{ background: `${c}22` }}
          data-testid={`doc-thumb-${doc.id}`}
          disabled={!previewable}
        >
          {kind === "photo" ? (
            <img src={url} alt="" className="w-full h-full object-cover rounded-xl" />
          ) : (
            <Icon size={18} weight="duotone" color={c} />
          )}
        </button>
        <button
          type="button"
          onClick={previewable ? onPreview : null}
          disabled={!previewable}
          className="flex-1 min-w-0 text-left"
          data-testid={`doc-name-${doc.id}`}
        >
          {doc.label && <div className="text-[10px] uppercase tracking-widest" style={{ color: c }}>{doc.label}</div>}
          <div className="text-sm font-medium truncate">{doc.filename}</div>
          <div className="text-[10px] text-white/40">
            {new Date(doc.created_at).toLocaleString("fr-FR")} · {doc.uploaded_by_name} · {Math.round((doc.size || 0) / 1024)} Ko
          </div>
        </button>
        <a
          href={url}
          download={doc.filename}
          className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 hover:border-white/25"
          title="Télécharger"
          data-testid={`doc-download-${doc.id}`}
        >
          <Download size={14} />
        </a>
        {onRemove && (
          <button
            onClick={onRemove}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:border-red-500/40 hover:text-red-400 flex items-center justify-center active:scale-95"
            title="Supprimer"
            data-testid={`doc-remove-${doc.id}`}
          >
            <Trash size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Full-screen preview overlay. Handles photo / video / pdf inline so the tech
 * never leaves the PWA. Close = big × top-right, tap-outside, Escape key, or
 * hardware back button (via history.pushState + popstate listener).
 */
function MediaLightbox({ doc, onClose }) {
  const kind = effectiveKind(doc);
  const url = fileUrl(doc.id);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    // Push a history entry so the hardware/browser back button closes the
    // lightbox instead of leaving the chantier detail page.
    try {
      window.history.pushState({ bkflowLightbox: true }, "");
    } catch (e) { console.debug("[docs] pushState failed:", e?.message || e); }
    const onPop = () => onClose();
    document.addEventListener("keydown", onKey);
    window.addEventListener("popstate", onPop);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("popstate", onPop);
      // If we're still in our pushed state, pop it silently so the URL is clean.
      if (window.history.state?.bkflowLightbox) {
        try { window.history.back(); } catch (e) { /* noop */ }
      }
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex flex-col animate-in fade-in"
      data-testid="doc-lightbox"
    >
      {/* Top bar with title + close */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-black/50 backdrop-blur-md">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-90 shrink-0"
          data-testid="doc-lightbox-close"
          title="Fermer (Échap)"
        >
          <X size={18} weight="bold" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Document</div>
          <div className="text-sm font-medium truncate" data-testid="doc-lightbox-title">{doc.filename}</div>
        </div>
        <a
          href={url}
          download={doc.filename}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-95 shrink-0"
          title="Télécharger"
          data-testid="doc-lightbox-download"
        >
          <Download size={16} />
        </a>
      </div>

      {/* Content area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {kind === "photo" && (
          <img
            src={url}
            alt={doc.filename}
            className="max-w-full max-h-full object-contain"
            onClick={onClose}
            data-testid="doc-lightbox-image"
          />
        )}
        {kind === "video" && (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="doc-lightbox-video"
          />
        )}
        {kind === "pdf" && (
          <iframe
            src={url}
            title={doc.filename}
            className="w-full h-full bg-white"
            data-testid="doc-lightbox-pdf"
          />
        )}
        {kind !== "photo" && kind !== "video" && kind !== "pdf" && (
          <div className="text-center text-white/70 px-6">
            <ArrowsOut size={32} className="mx-auto mb-2 text-white/40" weight="duotone" />
            <div className="text-sm">Ce format ne peut pas être prévisualisé dans l&apos;app.</div>
            <a href={url} download={doc.filename} className="mt-3 inline-block px-4 py-2 rounded-full bg-[#005CFF] text-white text-sm font-semibold active:scale-95">
              Télécharger « {doc.filename} »
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
